<div
    class="relative truncate"
    kendoTooltip
    position="top"
    tooltipClass="{{ error ? 'error-tooltip' : null }}"
    [tooltipTemplate]="error ? tooltipTemplate : null"
    [ngClass]="{ 'h-100': type === 'boolean' }">
    @if (label && type !== 'boolean') {
        <label class="flex select-none items-center justify-between text-xs font-normal text-neutral-400" [for]="label">
            <div class="flex items-center gap-2">
                <span>{{ label | translate }}</span>
            </div>
            @if (isClicked && maxlength) {
                <span class="relative">{{ characterCount }} / {{ maxlength }}</span>
            }
        </label>
    }
    @if (isEditMode) {
        @switch (type) {
            @case ('string') {
                <kendo-textbox
                    class="bg-input text-input-foreground"
                    type="text"
                    kendoTextBox
                    title=""
                    [placeholder]="placeholder"
                    [maxlength]="maxlength"
                    [disabled]="isDisabled"
                    [ngClass]="{
                        'is-invalid': !!error,
                        'brightness-75': isDisabled
                    }"
                    [(ngModel)]="value"
                    (focus)="focusInHandler()"
                    (blur)="focusOutHandler()"
                    (keypress)="onKeyPress($event)">
                    <ng-template kendoTextBoxSuffixTemplate>
                        <ng-content select=".textbox-suffix"></ng-content>
                    </ng-template>
                </kendo-textbox>
            }
            @case ('password') {
                <kendo-textbox
                    class="bg-input text-input-foreground"
                    title=""
                    [type]="showPassword() ? 'text' : 'password'"
                    [placeholder]="placeholder || ''"
                    [disabled]="isDisabled"
                    [ngClass]="{
                        'is-invalid': !!error,
                        'brightness-75': isDisabled
                    }"
                    [(ngModel)]="value">
                    <ng-template kendoTextBoxSuffixTemplate>
                        <button
                            class="group flex items-center justify-center rounded px-2"
                            type="button"
                            [attr.aria-label]="showPassword() ? 'Hide password' : 'Show password'"
                            [title]="showPassword() ? 'Hide password' : 'Show password'"
                            (click)="togglePasswordVisibility()">
                            <ng-icon
                                class="text-base text-muted group-hover:text-foreground"
                                [name]="showPassword() ? 'faSolidEyeSlash' : 'faSolidEye'"></ng-icon>
                        </button>
                    </ng-template>
                </kendo-textbox>
            }
            @case ('textarea') {
                <kendo-textarea
                    class="bg-input text-input-foreground"
                    #textarea
                    title=""
                    [attr.readonly]="isDisabled ? '' : null"
                    [maxlength]="maxlength"
                    [placeholder]="placeholder"
                    [disabled]="isDisabled"
                    [ngClass]="{ 'is-invalid': !!error, 'brightness-75': isDisabled }"
                    [rows]="rows"
                    [(ngModel)]="value"
                    (focus)="focusInHandler()"
                    (blur)="focusOutHandler()"></kendo-textarea>
            }
            @case ('number') {
                <kendo-numerictextbox
                    class="bg-input text-input-foreground"
                    title=""
                    [maxlength]="maxlength"
                    [format]="format"
                    [min]="min === undefined ? 0 : min"
                    [max]="max"
                    [autoCorrect]="true"
                    [placeholder]="placeholder"
                    [disabled]="isDisabled"
                    [ngClass]="{ 'is-invalid': !!error, 'brightness-75': isDisabled }"
                    [decimals]="decimals"
                    [readonly]="isDisabled"
                    [spinners]="showSpinners"
                    [(ngModel)]="value"
                    (focus)="focusInHandler()"
                    (blur)="focusOutHandler()"></kendo-numerictextbox>
            }
            @case ('boolean') {
                <div class="checkbox flex flex-col">
                    <label
                        *ngIf="label"
                        class="mb-1 select-none text-xs font-normal text-muted hover:cursor-pointer"
                        [for]="label">
                        {{ label | translate }}
                    </label>
                    <input
                        type="checkbox"
                        kendoCheckBox
                        #checkbox
                        [id]="label"
                        [indeterminate]="nullable ? booleanValue === null : false"
                        [disabled]="isDisabled"
                        [(ngModel)]="booleanValue" />
                </div>
            }
            @case ('date') {
                <div>
                    @if (required !== true && dateValue && !isDisabled) {
                        <span
                            class="absolute right-9 top-[27px] z-10 cursor-pointer italic text-input-foreground"
                            title="{{ 'Clear' | translate }}"
                            (click)="dateValue = null">
                            <ng-icon name="faSolidXmark" />
                        </span>
                    }
                    <kendo-datepicker
                        class="bg-input text-input-foreground"
                        #datePicker
                        placeholder=""
                        title=""
                        format="{{ defaultDateFormat }}"
                        [navigation]="false"
                        [readonly]="isDisabled"
                        [ngClass]="{ 'is-invalid': !!error, 'brightness-75': isDisabled }"
                        [disabled]="isDisabled"
                        [disabledDates]="disabledDates"
                        [min]="minDate"
                        [max]="maxDate"
                        [(ngModel)]="dateValue"
                        (focus)="focusInHandler()"
                        (blur)="focusOutHandler()">
                        <kendo-datetimepicker-messages
                            today="{{ 'Today' | translate }}"
                            dateTab="{{ 'Date' | translate }}"
                            timeTab="{{ 'Time' | translate }}"
                            now="{{ 'Now' | translate }}"
                            accept="{{ 'Accept' | translate }}"
                            cancel="{{ 'Cancel' | translate }}"></kendo-datetimepicker-messages>
                    </kendo-datepicker>
                </div>
            }
            @case ('datetime') {
                <div>
                    @if (required !== true && dateValue && !isDisabled) {
                        <span
                            class="absolute right-9 top-[27px] z-10 cursor-pointer italic text-input-foreground"
                            title="{{ 'Clear' | translate }}"
                            (click)="dateValue = null">
                            <ng-icon name="faSolidXmark" />
                        </span>
                    }
                    <kendo-datetimepicker
                        class="bg-input text-input-foreground"
                        #dateTimePicker
                        placeholder=""
                        format="{{ defaultDateTimeFormat }}"
                        title=""
                        [readonly]="isDisabled"
                        [ngClass]="{ 'is-invalid': !!error, 'brightness-75': isDisabled }"
                        [disabled]="isDisabled"
                        [disabledDates]="disabledDates"
                        [min]="minDate"
                        [max]="maxDate"
                        [(ngModel)]="dateValue"
                        (focus)="focusInHandler()"
                        (blur)="focusOutHandler()">
                        <kendo-datetimepicker-messages
                            today="{{ 'Today' | translate }}"
                            dateTab="{{ 'Date' | translate }}"
                            timeTab="{{ 'Time' | translate }}"
                            now="{{ 'Now' | translate }}"
                            accept="{{ 'Accept' | translate }}"
                            cancel="{{ 'Cancel' | translate }}"></kendo-datetimepicker-messages>
                    </kendo-datetimepicker>
                </div>
            }
            @case ('yesno') {
                <kendo-combobox
                    class="bg-input text-input-foreground"
                    title=""
                    [textField]="textField"
                    [valueField]="valueField"
                    [valuePrimitive]="true"
                    [data]="filteredOptions"
                    [filterable]="false"
                    [disabled]="isDisabled"
                    [clearButton]="required !== true"
                    [ngClass]="{ 'is-invalid': !!error, 'brightness-75': isDisabled }"
                    [(ngModel)]="value" />
            }
            @case ('file') {
                <kendo-fileselect
                    title=""
                    [ngClass]="{ 'is-invalid': !!error, disabled: isDisabled }"
                    [multiple]="multi"
                    [disabled]="isDisabled"
                    [restrictions]="fileRestrictions"
                    [(ngModel)]="value"
                    (select)="fileSelect($event)">
                    <kendo-fileselect-messages
                        [select]="multi ? ('Select files...' | translate) : ('Select file...' | translate)"
                        [invalidFileExtension]="'Uploading this file type is not allowed' | translate"
                        [dropFilesHere]="'Drop files here to select' | translate"></kendo-fileselect-messages>
                </kendo-fileselect>
            }
        }
        <!-- we use @if because @switch does not support case with multiple values yet -->
        @if (type === 'codelist') {
            @if (multi) {
                <div *subscribe="query$ as query">
                    <kendo-multiselect
                        class="bg-input text-input-foreground"
                        #multiselect
                        [textField]="textField"
                        [valueField]="valueField"
                        [valuePrimitive]="true"
                        [data]="filteredOptions"
                        [filterable]="isFilterable"
                        [loading]="query?.isLoading"
                        [readonly]="isDisabled || query?.isLoading"
                        [popupSettings]="popupSettings"
                        [clearButton]="clearButton"
                        [disabled]="isDisabled"
                        [autoClose]="false"
                        [ngClass]="{
                            'is-invalid': !!error,
                            'brightness-75': isDisabled || query?.isLoading,
                            required: required
                        }"
                        [(ngModel)]="value"
                        (mouseenter)="isMouseEnter = true"
                        (mouseleave)="isMouseEnter = false"
                        (focus)="focusInHandler()"
                        (blur)="focusOutHandler()"
                        (open)="onDropdownOpen()"
                        (filterChange)="filterCodeList($event)"
                        (ngModelChange)="ngModelChange.emit($event)">
                        <ng-template kendoMultiSelectItemTemplate let-dataItem>
                            <div class="p-1">
                                <label class="k-checkbox-label">{{ dataItem.label | translate }}</label>
                            </div>
                        </ng-template>
                    </kendo-multiselect>
                    @if (!(query?.isLoading || isMouseEnter) && value?.length > 0) {
                        <span
                            class="absolute right-1 top-6 text-input-foreground"
                            [ngClass]="{ 'right-5': !required && !isDisabled }">
                            ({{ value.length }})
                        </span>
                    }
                </div>
            } @else {
                <kendo-combobox
                    class="bg-input text-input-foreground"
                    title=""
                    data-container="body"
                    *subscribe="query$ as query"
                    [textField]="textField"
                    [valueField]="valueField"
                    [valuePrimitive]="true"
                    [data]="filteredOptions"
                    [filterable]="isFilterable"
                    [disabled]="isDisabled"
                    [readonly]="isDisabled || query?.isLoading"
                    [clearButton]="clearButton"
                    [loading]="query?.isLoading"
                    [popupSettings]="popupSettings"
                    [ngClass]="{
                        'is-invalid': !!error,
                        'brightness-75': isDisabled || query?.isLoading,
                        isLoading: query?.isLoading
                    }"
                    [(ngModel)]="value"
                    (focus)="focusInHandler()"
                    (blur)="focusOutHandler()"
                    (open)="onDropdownOpen()"
                    (filterChange)="filterCodeList($event)" />
            }
        }
        @if (type === 'select') {
            <kendo-combobox
                class="bg-input text-input-foreground"
                title=""
                data-container="body"
                [textField]="textField"
                [valueField]="valueField"
                [valuePrimitive]="true"
                [disabled]="isDisabled"
                [data]="filteredOptions"
                [value]="value"
                [clearButton]="clearButton"
                [filterable]="isFilterable"
                [readonly]="isDisabled"
                [popupSettings]="popupSettings"
                [ngClass]="{
                    'is-invalid': !!error,
                    'brightness-75': isDisabled
                }"
                [(ngModel)]="value"
                (open)="onDropdownOpen()"
                (ngModelChange)="ngModelChange.emit($event)"
                (filterChange)="filterCodeList($event)" />
        }
    } @else {
        <div class="truncate pb-1 pt-1.5">
            @if (value && !['yesno', 'boolean'].includes(type)) {
                @switch (type) {
                    @case ('select') {
                        <span title="{{ getTooltip(value, selectViewValue) }}">{{ selectViewValue }}</span>
                    }
                    @case ('codelist') {
                        @if (value | codelist: codelist | async; as codelistItem) {
                            @if (customRoute) {
                                <app-link [route]="[customRoute.path, customRoute.field]" [checkPermission]="false">
                                    {{ codelistItem }}
                                </app-link>
                            } @else {
                                <span title="{{ getTooltip(value, codelistItem) }}">{{ codelistItem }}</span>
                            }
                        }
                    }
                    @case ('number') {
                        {{ value | kendoFormat: format }}
                    }
                    @case ('datetime') {
                        {{ value | datetime }}
                    }
                    @case ('date') {
                        {{ value | customDate }}
                    }
                    @case ('textarea') {
                        <div class="whitespace-pre-wrap">{{ value }}</div>
                    }
                    @default {
                        {{ value }}
                    }
                }
            } @else if (['yesno', 'boolean'].includes(type)) {
                @if (type === 'boolean') {
                    <label class="block select-none pb-1 text-xs font-normal text-muted" [for]="label">
                        {{ label | translate }}
                    </label>
                }
                @if (value === true) {
                    <ng-icon class="text-success" name="faSolidCheck" />
                } @else if (value === false) {
                    <ng-icon class="text-danger" name="faSolidXmark" />
                } @else {
                    -
                }
            } @else {
                -
            }
        </div>
    }
</div>

<ng-template #tooltipTemplate>
    <span>{{ error | translate }}</span>
</ng-template>
